import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { motion, MotionConfig } from "motion/react";
import { useTranslation } from "react-i18next";
import { PageMeta } from "../../PageMeta";
import { LogoMark } from "./LogoMark";
import { LocaleSwitcher } from "./LocaleSwitcher";
import "./auth-motion.css";

interface AuthShellProps {
  pageTitle: string;
  pageDescription: string;
  /** Left brand panel — omit for single-column centered layout (token pages). */
  brand?: ReactNode;
  /** Optional top-right action (e.g. "Don't have an account? Sign up"). */
  headerAction?: ReactNode;
  /** When true, the centered-card variant uses the wider split layout
   *  (~720px) instead of the standard single-column (~440px). */
  wide?: boolean;
  children: ReactNode;
}

/**
 * AuthShell — top-level layout for every auth page.
 *
 * Two-column on desktop (≥1024px): brand panel on `--sidebar`, action area
 * on `--background`. Below 1024px: brand panel hidden; mobile brand pins
 * inside the action area.
 *
 * If `brand` is omitted, renders the single-column centered layout used
 * by token-only pages (CompleteSignUp, ResetPassword, VerifyEmail,
 * MagicLink, AcceptInvite).
 *
 * MotionConfig honors prefers-reduced-motion at the OS level.
 */
export function AuthShell({
  pageTitle,
  pageDescription,
  brand,
  headerAction,
  wide = false,
  children,
}: AuthShellProps) {
  const [logoHovered, setLogoHovered] = useState(false);
  const { t } = useTranslation();

  // Page footer — status pill on the left, legal links + locale switcher
  // on the right. Bottom-anchored over the viewport, consistent across
  // all 7 pages. Mercury / Vercel / Cloudflare hybrid pattern.
  const pageFooter = (
    <footer className="auth-page-footer">
      <a
        href="https://status.adjudon.com"
        className="auth-status-pill"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="System status — opens in a new tab"
      >
        <span className="auth-status-dot adp-live" aria-hidden="true" />
        <span>{t("common.footer.status")}</span>
      </a>
      <div className="flex items-center gap-2 flex-wrap">
        <nav className="auth-footer-links" aria-label="Legal and privacy">
          <Link to="/legal/imprint">{t("common.footer.imprint")}</Link>
          <span className="auth-footer-sep" aria-hidden="true">·</span>
          <Link to="/legal/privacy">{t("common.footer.privacy")}</Link>
          <span className="auth-footer-sep" aria-hidden="true">·</span>
          <Link to="/legal/terms">{t("common.footer.terms")}</Link>
          <span className="auth-footer-sep" aria-hidden="true">·</span>
          <Link to="/legal/dpa">{t("common.footer.dpa")}</Link>
        </nav>
        <LocaleSwitcher />
      </div>
    </footer>
  );

  // Top-left corner brand mark + optional top-right action. Shared
  // between the single-column centered variant and would-be brand-panel
  // pages whose action area is in the right column.
  const cornerHeader = (
    <header className="auth-page-header">
      <Link
        to="/"
        className="flex items-center gap-2.5 focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--brand)] focus-visible:outline-offset-2"
        aria-label="Adjudon home"
        onMouseEnter={() => setLogoHovered(true)}
        onMouseLeave={() => setLogoHovered(false)}
        onFocus={() => setLogoHovered(true)}
        onBlur={() => setLogoHovered(false)}
      >
        <LogoMark hovered={logoHovered} size={28} />
        <span
          className="font-semibold tracking-tight text-base"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Adjudon
        </span>
      </Link>
      {headerAction && <div className="flex-shrink-0">{headerAction}</div>}
    </header>
  );

  if (!brand) {
    return (
      <MotionConfig reducedMotion="user">
        <div className="auth-fullheight relative bg-[var(--background)] text-[var(--foreground)] flex flex-col items-center px-6 pt-28 pb-32 lg:pt-32">
          <PageMeta title={pageTitle} description={pageDescription} noindex={true} />
          {cornerHeader}

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.3, 0, 0, 1] }}
            className={wide ? "auth-card-stack-wide" : "auth-card-stack"}
          >
            {children}
          </motion.div>

          {pageFooter}
        </div>
      </MotionConfig>
    );
  }

  // Mobile-only brand mark for the brand-panel branch. Without this,
  // mobile users on AcceptInvite (the only page that uses the brand-panel
  // layout) would see no Adjudon wordmark at all — the brand panel
  // <aside> is hidden via `lg:flex` below 1024px, and this layout branch
  // does NOT render the corner-header. Match the Login/SignUp mobile
  // brand position: pinned to top of the action area.
  const mobileBrand = (
    <Link
      to="/"
      className="flex lg:hidden items-center gap-2.5 mb-6 self-start focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--brand)] focus-visible:outline-offset-2"
      aria-label="Adjudon home"
      onMouseEnter={() => setLogoHovered(true)}
      onMouseLeave={() => setLogoHovered(false)}
      onFocus={() => setLogoHovered(true)}
      onBlur={() => setLogoHovered(false)}
    >
      <LogoMark hovered={logoHovered} size={28} />
      <span
        className="font-semibold tracking-tight text-base"
        style={{ fontFamily: "var(--font-display)" }}
      >
        Adjudon
      </span>
    </Link>
  );

  return (
    <MotionConfig reducedMotion="user">
      <div className="auth-fullheight relative bg-[var(--background)] text-[var(--foreground)] grid grid-cols-1 lg:grid-cols-2">
        <PageMeta title={pageTitle} description={pageDescription} noindex={true} />

        {/* LEFT — brand + value copy. Hidden on mobile. */}
        <aside className="hidden lg:flex auth-brand-panel">
          <div className="flex flex-col w-full">
            <Link
              to="/"
              className="inline-flex items-center gap-3 mb-12 self-start focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--brand)] focus-visible:outline-offset-2"
              aria-label="Adjudon home"
              onMouseEnter={() => setLogoHovered(true)}
              onMouseLeave={() => setLogoHovered(false)}
              onFocus={() => setLogoHovered(true)}
              onBlur={() => setLogoHovered(false)}
            >
              <LogoMark hovered={logoHovered} />
              <div className="leading-tight">
                <span
                  className="block font-semibold text-lg tracking-tight"
                  style={{ fontFamily: "var(--font-display)" }}
                >
                  Adjudon
                </span>
                <span className="block auth-mono-label">AI Governance Firewall</span>
              </div>
            </Link>

            <div className="flex-1">{brand}</div>
          </div>
        </aside>

        {/* RIGHT — action area. Mobile-brand pinned above the form
            since the desktop brand panel is hidden on <1024px. */}
        <main className="flex items-start lg:items-center justify-center px-6 pt-10 pb-32 lg:py-12 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.3, 0, 0, 1], delay: 0.1 }}
            className="auth-card-stack flex flex-col"
          >
            {mobileBrand}
            {children}
          </motion.div>
        </main>

        {pageFooter}
      </div>
    </MotionConfig>
  );
}
